import { type FC, useEffect, useRef } from "react";

import type { MentionTarget } from "./mentions";

import { t } from "i18n/i18n";
import { InitialsAvatar } from "mds/components/InitialsAvatar";
import {
  SELECTION_LIST_OPTION_CLASSES,
  SELECTION_LIST_OPTION_LABEL_CLASSES,
  SELECTION_LIST_SURFACE_CLASSES,
} from "mds/components/selection-list-styles";
import { PersonClassIcon, PersonGroupIcon } from "mds/icons";

type MentionSuggestionsProps = {
  activeIndex: number;
  id: string;
  onSelect: (target: MentionTarget) => void;
  targets: MentionTarget[];
};

const getMentionTargetLabel = (target: MentionTarget) => {
  if (target.kind === "all") return t("chat.mention_all");
  if (target.kind === "breakout") {
    return t("chat.mention_breakout", {
      number: target.mentionText.replace("breakout", ""),
    });
  }
  return target.displayName;
};

const MentionTargetIcon: FC<{ isSelected: boolean; target: MentionTarget }> = ({
  isSelected,
  target,
}) => {
  if (target.kind === "all") {
    return (
      <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center">
        <PersonClassIcon className={isSelected ? "text-white" : "text-blue"} size="s" />
      </span>
    );
  }

  if (target.kind === "breakout") {
    return (
      <span
        aria-hidden="true"
        className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
          isSelected ? "" : "bg-hibiscus-tint-95"
        }`}
      >
        <PersonGroupIcon className={isSelected ? "text-white" : "text-hibiscus"} size="xs" />
      </span>
    );
  }

  return <InitialsAvatar aria-hidden="true" initials={target.initials} isSelected={isSelected} />;
};

export const MentionSuggestions: FC<MentionSuggestionsProps> = ({
  activeIndex,
  id,
  onSelect,
  targets,
}) => {
  const activeOptionRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  return (
    <ul
      aria-label={t("chat.mention_suggestions")}
      className={`${SELECTION_LIST_SURFACE_CLASSES} absolute bottom-[calc(100%+0.5rem)] left-0 z-10 max-h-[296px] w-full`}
      id={id}
      role="listbox"
      onMouseDown={(event) => event.preventDefault()}
    >
      {targets.map((target, index) => {
        const isSelected = index === activeIndex;
        return (
          <li
            aria-selected={isSelected}
            className={SELECTION_LIST_OPTION_CLASSES}
            data-focus={isSelected ? "" : undefined}
            data-selected={isSelected ? "" : undefined}
            data-testid="mention-option"
            id={`${id}-option-${index}`}
            key={target.id}
            ref={isSelected ? activeOptionRef : undefined}
            role="option"
            onMouseDown={() => onSelect(target)}
          >
            <MentionTargetIcon isSelected={isSelected} target={target} />
            <span className={SELECTION_LIST_OPTION_LABEL_CLASSES}>
              {getMentionTargetLabel(target)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
